import { getLocationAsText } from '@src/utils';
import {
  ControlledDateSelect,
  ControlledInput,
} from '@uikit/FormItems/ControlledItems';
import Modal from '@uikit/Modal/Modal';
import TableSection from '@uikit/Tables/TableSection/TableSection';
import { validations } from '@utils/validations';
import { FC } from 'react';
import { useMemberSearch } from '@hooks/useMemberSearch';

interface IMemberSearchModal {
  closeModal: () => void;
}

const MemberSearchModal: FC<IMemberSearchModal> = ({ closeModal }) => {
  const {
    control,
    searchMemberData,
    handleChangeMemberAddress,
    handleClickEdit,
    filters,
    isLoading,
    data,
    handleSelectAddress,
    debouncedAddressValues,
  } = useMemberSearch();

  return (
    <Modal
      title="Member Search"
      closeClick={closeModal}
      maxWidth="980px"
      dataTestId="msm"
    >
      <div className="search-wrapper member-search-wrapper" data-test-id="msm">
        <div
          className="modal-search-from member-inputs-wrapper"
          data-test-id="msm-form"
        >
          <div className="first-row" data-test-id="msm-row1">
            <div className="modal-search-item">
              <ControlledInput
                name="firstName"
                title="First Name"
                control={control}
                dataTestId="msm-fn"
              />
            </div>
            <div className="modal-search-item">
              <ControlledInput
                name="lastName"
                title="Last Name"
                control={control}
                dataTestId="msm-ln"
              />
            </div>
            <div className="modal-search-item">
              <ControlledDateSelect
                control={control}
                name="dob"
                title="Date of Birth"
                dataTestId="msm-dob"
              />
            </div>
            <div className="modal-search-item">
              <ControlledInput
                control={control}
                name="memberIdNumber"
                title="Member ID"
                dataTestId="msm-mid"
              />
            </div>
          </div>
          <div className="second-row" data-test-id="msm-row2">
            <div className="modal-search-item">
              <ControlledInput
                name="phone"
                mask="(999) 999-9999"
                title="Contact Number"
                control={control}
                rules={{
                  validate: {
                    exactLength: (value) =>
                      validations.length('Primary Contact Number', value, 10),
                  },
                }}
                dataTestId="msm-phone"
              />
            </div>
            <div className="modal-search-item">
              <ControlledInput
                name="email"
                title="Email Address"
                control={control}
                rules={{
                  pattern: {
                    value: validations.email('Email Address').pattern,
                    message: validations.email('Email Address').message,
                  },
                }}
                dataTestId="msm-email"
              />
            </div>
            <div className="modal-search-item member-last-search-item">
              <ControlledInput
                name="address"
                type="text"
                title="Mailing/Physical Address"
                control={control}
                onChange={(name, value) =>
                  handleChangeMemberAddress(name, value as any)
                }
                dataTestId="msm-addr"
              />

              {debouncedAddressValues &&
                (data && data.locations.length > 0 ? (
                  <div
                    className="location-select__item_member"
                    data-test-id="msm-addr-list"
                  >
                    {data.locations.map((elem) => (
                      <div
                        key={elem.id}
                        className="location-select__item_member__text"
                        data-test-id={`msm-addr-opt-${elem.id}`}
                        onClick={() => {
                          handleSelectAddress(elem);
                        }}
                      >
                        {getLocationAsText(elem)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span
                    className="not-found-address"
                    data-test-id="msm-addr-nf"
                  >
                    Address not found
                  </span>
                ))}
            </div>
          </div>
        </div>

        {[
          {
            ...searchMemberData,
            title: 'Search Result',
            isLoading: isLoading,
            argument: { body: filters },
          },
        ].map((config) => (
          <TableSection
            key={config.title}
            dataTestId={`table-${config.title}`}
            title={config.title}
            isLoading={config.isLoading}
            openModal={config.openModal}
            permissions={config.permissions}
            requestTablePageChange={config.requestTablePageChange}
            tableCustomClassName={[
              'member-search-results-table',
              config.tableCustomClassName,
            ]
              .filter(Boolean)
              .join(' ')}
            hasCreatePermission={false}
            showTable={true}
            tableName={config.table}
            sortBy={config.sortBy}
            sortDir={config.sortDir}
            onEditClick={config.handleEditClick}
            rows={config.rows}
            page={config.page}
            columns={config.columns}
            totalElements={config.totalElements}
            size={config.size}
            totalPages={config.totalPages}
            modalName={config.modalName}
            addBtnTitle={config.addBtnTitle}
            argument={config.argument}
            isFilterable={false}
            arrowIconMember={true}
            handleSelectedRow={handleClickEdit}
            infiniteScroll={true}
          />
        ))}
      </div>
    </Modal>
  );
};

export default MemberSearchModal;
